import type { EventMapDocument, EventMapUnitView } from '~/shared-kernel/presentation/event-map/event-map.types'

export interface EventMapUnitVisualState {
  hoveredUnitId: string | null
  inspectedUnitId: string | null
  ownedUnitIds: ReadonlySet<string>
  pendingUnitIds: ReadonlySet<string>
}

export interface EventMapUnitVisualStateInput {
  hoveredUnitId?: string | null
  inspectedUnitId?: string | null
  ownedUnitIds?: readonly string[]
  pendingUnitIds?: readonly string[]
}

export interface EventMapUnitRenderState {
  blocked: boolean
  owned: boolean
  pending: boolean
  outlineColor: number | null
  canSelect: boolean
}

export function createEventMapUnitVisualState(input: EventMapUnitVisualStateInput = {}): EventMapUnitVisualState {
  return {
    hoveredUnitId: input.hoveredUnitId ?? null,
    inspectedUnitId: input.inspectedUnitId ?? null,
    ownedUnitIds: new Set(input.ownedUnitIds ?? []),
    pendingUnitIds: new Set(input.pendingUnitIds ?? [])
  }
}

export function resolveEventMapUnitRenderState(
  unitId: string,
  available: boolean | undefined,
  visualState: EventMapUnitVisualState
): EventMapUnitRenderState {
  const owned = visualState.ownedUnitIds.has(unitId)
  const pending = !owned && visualState.pendingUnitIds.has(unitId)
  const blocked = available === false && !owned
  // Owned units get a dedicated dark fill instead of an outline; only hover/inspection/pending use the ring.
  const highlightedForOutline = !owned
    && (pending
      || visualState.hoveredUnitId === unitId
      || visualState.inspectedUnitId === unitId)

  return {
    blocked,
    owned,
    pending,
    outlineColor: highlightedForOutline ? 0x14b8a6 : null,
    canSelect: !blocked
  }
}

/**
 * Returns only units whose hover, inspection, ownership or pending membership
 * may have changed. Complexity is O(P + N), where P/N are the previous/next
 * membership sizes, independent from the total number of units in the map.
 */
export function changedEventMapUnitVisualIds(
  previous: EventMapUnitVisualState,
  next: EventMapUnitVisualState
): Set<string> {
  const changed = new Set<string>()

  if (previous.hoveredUnitId !== next.hoveredUnitId) {
    if (previous.hoveredUnitId) changed.add(previous.hoveredUnitId)
    if (next.hoveredUnitId) changed.add(next.hoveredUnitId)
  }
  if (previous.inspectedUnitId !== next.inspectedUnitId) {
    if (previous.inspectedUnitId) changed.add(previous.inspectedUnitId)
    if (next.inspectedUnitId) changed.add(next.inspectedUnitId)
  }
  for (const key of ['ownedUnitIds', 'pendingUnitIds'] as const) {
    if (previous[key] === next[key]) continue
    for (const id of previous[key]) {
      if (!next[key].has(id)) changed.add(id)
    }
    for (const id of next[key]) {
      if (!previous[key].has(id)) changed.add(id)
    }
  }

  return changed
}

function sameMapStructure(previous: unknown, next: unknown): boolean {
  if (previous === next) return true
  if (!previous || !next || typeof previous !== 'object' || typeof next !== 'object') return false
  if (Array.isArray(previous) !== Array.isArray(next)) return false
  if (Array.isArray(previous) && previous.length !== (next as unknown[]).length) return false
  const before = previous as Record<string, unknown>
  const after = next as Record<string, unknown>
  let beforeCount = 0
  let afterCount = 0
  for (const key in before) {
    if (!Object.hasOwn(before, key) || key === 'available' || key === 'blocked') continue
    beforeCount++
    if (!Object.hasOwn(after, key) || !sameMapStructure(before[key], after[key])) return false
  }
  for (const key in after) {
    if (Object.hasOwn(after, key) && key !== 'available' && key !== 'blocked') afterCount++
  }
  return beforeCount === afterCount
}

/** Null requires rebuilding geometry; otherwise repaint only these live units. */
export function changedEventMapDocumentUnits(
  previous: EventMapDocument,
  next: EventMapDocument
): Map<string, EventMapUnitView> | null {
  if (!sameMapStructure(previous, next)) return null
  if (next.localities.some((locality, index) => locality.available !== previous.localities[index]?.available)) return null
  const changed = new Map<string, EventMapUnitView>()
  for (const sectionId in next.seatLayouts) {
    const units = next.seatLayouts[sectionId]!.units
    const before = previous.seatLayouts[sectionId]!.units
    for (let index = 0; index < units.length; index++) {
      const unit = units[index]!
      const old = before[index]!
      if (unit.available !== old.available || unit.blocked !== old.blocked) changed.set(unit.id, unit)
    }
  }
  return changed
}
